import { T, useGT } from 'gt-react';
import { XIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { TextInput } from '@/components/common/TextInput';
import {
  AR_COLLECTION_ACTION_SCOPES,
  stableKeyFromLabel,
  type ArCollectionActionDefinition,
  type ArCollectionActionScope,
} from '@erp/shared-types';

/**
 * Editor for the AI recommendation action catalog — the set of collection
 * actions the recommendation agent may suggest, and the prompt description that
 * steers when each applies. Used org-wide (Settings → Modules) and per customer
 * (collections profile). Purely presentational: it renders `value` and emits
 * the full edited list via `onChange`; the container owns save + validation
 * gating (see the exported validity/finalize helpers).
 */

const SCOPE_LABELS: Record<ArCollectionActionScope, string> = {
  short_pay: 'Short pay',
  over_payment: 'Over-payment',
};

/** A blank row for a newly-added custom action. */
export function newRecommendationActionRow(): ArCollectionActionDefinition {
  return {
    id: '',
    label: '',
    description: '',
    enabled: true,
    appliesTo: ['short_pay'],
    emailable: false,
  };
}

/** Derive a stable snake_case id from a label, avoiding already-taken ids. */
function actionIdFromLabel(label: string, taken: Set<string>): string {
  return stableKeyFromLabel(label, taken, 'action');
}

/** True when any row is incomplete (empty label/description, or no scope). */
export function recommendationActionsInvalid(
  rows: ArCollectionActionDefinition[],
): boolean {
  return rows.some(
    (row) =>
      row.label.trim() === '' ||
      row.description.trim() === '' ||
      row.appliesTo.length === 0,
  );
}

/**
 * Normalize rows for persistence: trim text and fill a stable id (derived from
 * the label) for any row that doesn't already carry one. Existing ids are kept
 * so relabeling doesn't orphan dismissals stamped on the old id.
 */
export function finalizeRecommendationActions(
  rows: ArCollectionActionDefinition[],
): ArCollectionActionDefinition[] {
  const taken = new Set(rows.map((r) => r.id).filter((id) => id !== ''));
  return rows.map((row) => {
    const id = row.id !== '' ? row.id : actionIdFromLabel(row.label, taken);
    taken.add(id);
    return {
      id,
      label: row.label.trim(),
      description: row.description.trim(),
      enabled: row.enabled,
      appliesTo: row.appliesTo,
      emailable: row.emailable ?? false,
    };
  });
}

interface Props {
  value: ArCollectionActionDefinition[];
  onChange: (next: ArCollectionActionDefinition[]) => void;
  disabled: boolean;
}

export function RecommendationActionsEditor({
  value,
  onChange,
  disabled,
}: Props) {
  const gt = useGT();

  const update = (
    index: number,
    patch: Partial<ArCollectionActionDefinition>,
  ) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const toggleScope = (index: number, scope: ArCollectionActionScope) => {
    const row = value[index];
    const appliesTo = row.appliesTo.includes(scope)
      ? row.appliesTo.filter((s) => s !== scope)
      : [...row.appliesTo, scope];
    update(index, { appliesTo });
  };

  return (
    <div className="space-y-2">
      <Label>
        <T context="settings field label for the AI recommendation action catalog">
          Recommended actions
        </T>
      </Label>
      <p className="text-xs text-muted-foreground">
        <T context="settings help text for the recommendation action catalog">
          The actions the AI may recommend on short-pay / over-payment threads.
          The description tells the AI when each action fits — edit it to steer
          the recommendations. Disable an action to stop suggesting it; add your
          own for advisory guidance.
        </T>
      </p>

      <div className="space-y-3">
        {value.map((row, index) => (
          <div
            key={row.id || `new-${index}`}
            className="space-y-2 rounded-md border p-3"
          >
            <div className="flex items-center gap-2">
              <Switch
                checked={row.enabled}
                onCheckedChange={(enabled) => update(index, { enabled })}
                disabled={disabled}
                aria-label={gt('Enable this action', {
                  context: 'toggle label to enable a recommendation action',
                })}
              />
              <Input
                type="text"
                value={row.label}
                placeholder={gt('Label (e.g. Send Payment Reminder)', {
                  context:
                    'input placeholder for a recommendation action label',
                })}
                aria-invalid={row.label.trim() === '' || undefined}
                onChange={(e) => update(index, { label: e.target.value })}
                disabled={disabled}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={gt('Remove action', {
                  context:
                    'button aria-label for removing a recommendation action',
                })}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                disabled={disabled}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>

            <TextInput
              value={row.description}
              onValueChange={(description) => update(index, { description })}
              rows={2}
              aria-invalid={row.description.trim() === '' || undefined}
              placeholder={gt(
                'Description for the AI — when does this action fit?',
                {
                  context:
                    'input placeholder for a recommendation action description',
                },
              )}
              className="text-xs"
            />

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  <T context="label for which thread types an action applies to">
                    Applies to
                  </T>
                </span>
                {AR_COLLECTION_ACTION_SCOPES.map((scope) => (
                  <label
                    key={scope}
                    className="flex items-center gap-1.5 text-xs"
                  >
                    <Checkbox
                      checked={row.appliesTo.includes(scope)}
                      onCheckedChange={() => toggleScope(index, scope)}
                      disabled={disabled}
                    />
                    {SCOPE_LABELS[scope]}
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-1.5 text-xs">
                <Checkbox
                  checked={row.emailable ?? false}
                  onCheckedChange={(checked) =>
                    update(index, { emailable: checked === true })
                  }
                  disabled={disabled}
                />
                <T context="toggle label, action offers a draft-email button">
                  Offers "Draft email"
                </T>
              </label>
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...value, newRecommendationActionRow()])}
        disabled={disabled}
      >
        <T context="button label for adding a recommendation action">
          Add action
        </T>
      </Button>
    </div>
  );
}
